import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref, shallowRef } from 'vue'
import i18n from '../../../../VueI18n'
import type { Map as MapboxMap } from 'mapbox-gl'
import { bbox as turfBbox } from '@turf/turf'
import { GeoImagesViewModelKey } from '../keys/GeoImagesViewModel.key'
import { GeoImageOverlayProvider } from '../providers/GeoImageOverlay.provider'
import { CLIP_BUFFER_METERS } from '../providers/FarmClipBuffer.provider'
import Breadcrumb from '../../../../components/ui/breadcrumb/Breadcrumb.vue'
import MapBoxMap from '../../../ui/map/MapBoxMap.vue'
import MapBoxSource from '../../../ui/map/MapBoxSource.vue'
import MapBoxLayer from '../../../ui/map/MapBoxLayer.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'GeoImageValidationStep',
  setup(__props) {

const vm = inject(GeoImagesViewModelKey)!

const IMAGE_SOURCE_ID = 'geo-image-overlay'
const COVERAGE_SOURCE_ID = 'geo-image-coverage'
const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] }

const mapReady = ref(false)
const mapInstance = shallowRef<MapboxMap | null>(null)

/** TEMPORARIO (diagnostico): estado exibido no painel sobre o mapa. */
const diagnostico = ref<{ camadaAdicionada: boolean; erroMapbox: string }>({
	camadaAdicionada: false,
	erroMapbox: '',
})

const diagnosticoLinhas = computed(() => {
	const overlay = vm.imageOverlay.value
	const buffer = vm.clipBuffer.value
	const fazendaBbox = buffer?.features?.length
		? (turfBbox(buffer as never) as [number, number, number, number])
		: null

	const fmt = (b: [number, number, number, number] | null) =>
		b ? b.map((v) => v.toFixed(5)).join(', ') : '—'

	let sobrepoe = '—'
	if (overlay && fazendaBbox) {
		const [iw, is, ie, ineg] = overlay.bounds
		const [fw, fs, fe, fn] = fazendaBbox
		sobrepoe = iw < fe && ie > fw && is < fn && ineg > fs ? 'sim' : 'NÃO'
	}

	const km = (b: [number, number, number, number] | null) => {
		if (!b) return null
		const lat = ((b[1] + b[3]) / 2) * (Math.PI / 180)
		return {
			largura: (b[2] - b[0]) * 111.32 * Math.cos(lat),
			altura: (b[3] - b[1]) * 110.57,
		}
	}
	const kmImagem = km(overlay?.bounds ?? null)
	const kmFazenda = km(fazendaBbox)
	const bandas = vm.extractedBands.value
	const pixelsComDado = bandas
		? `${(GeoImageOverlayProvider.coverageRatio(bandas) * 100).toFixed(1)}%`
		: '—'

	const cobertura =
		kmImagem && kmFazenda
			? `${((kmImagem.largura * kmImagem.altura * 100) / (kmFazenda.largura * kmFazenda.altura)).toFixed(1)}%`
			: '—'

	return [
		['overlay gerado', overlay ? 'sim' : 'NÃO'],
		[
			'tamanho imagem',
			kmImagem ? `${kmImagem.largura.toFixed(2)} x ${kmImagem.altura.toFixed(2)} km` : '—',
		],
		['cobertura da fazenda', cobertura],
		['pixels com dado', pixelsComDado],
		['camada no mapa', diagnostico.value.camadaAdicionada ? 'sim' : 'NÃO'],
		['sobrepõe a fazenda', sobrepoe],
		['bounds imagem', fmt(overlay?.bounds ?? null)],
		['bounds fazenda', fmt(fazendaBbox)],
		['erro mapbox', diagnostico.value.erroMapbox || '—'],
	]
})

/**
 * O breadcrumb lista apenas as etapas anteriores: como formStep vale 2 aqui,
 * o proprio componente as marca como concluidas.
 */
const completedStep = computed(() => vm.formStep.value)

const farmSpec = computed(() => ({
	type: 'geojson' as const,
	data: (vm.farmMap.value?.fazenda ?? EMPTY_COLLECTION) as any,
}))

const bufferSpec = computed(() => ({
	type: 'geojson' as const,
	data: (vm.clipBuffer.value ?? EMPTY_COLLECTION) as any,
}))

/**
 * Contorno da area coberta pela imagem. Sem ele, uma imagem que cobre uma
 * fracao pequena da fazenda se confunde com o satelite e parece ausente.
 */
const imageBoundsSpec = computed(() => {
	const bounds = vm.imageOverlay.value?.bounds
	const data = bounds
		? {
				type: 'FeatureCollection',
				features: [
					{
						type: 'Feature',
						properties: { marcacao: 'imagem' },
						geometry: {
							type: 'Polygon',
							coordinates: [
								GeoImageOverlayProvider.toImageCoordinates(bounds).concat([
									GeoImageOverlayProvider.toImageCoordinates(bounds)[0],
								]),
							],
						},
					},
				],
			}
		: EMPTY_COLLECTION
	return { type: 'geojson' as const, data: data as any }
})

function enquadrarImagem() {
	const bounds = vm.imageOverlay.value?.bounds
	if (!bounds || !mapInstance.value) return
	mapInstance.value.fitBounds(bounds as any, { padding: 60 })
}

function enquadrarFazenda() {
	const alvo = vm.clipBuffer.value ?? vm.farmMap.value?.fazenda ?? null
	if (!alvo || !mapInstance.value) return
	mapInstance.value.fitBounds(turfBbox(alvo as never) as any, { padding: 24, maxZoom: 15 })
}

/** Presente so em raster de banda unica, onde a cor representa altura ou cota. */
const valueRange = computed(() => vm.imageOverlay.value?.valueRange ?? null)

const missingLayers = computed(() => {
	const missing: string[] = []
	if (!vm.hasFarmShape.value) missing.push(i18n.t('validacaoImagemCamadaFazenda') as string)
	if (!vm.imageOverlay.value || vm.imageRenderFailed.value) {
		missing.push(i18n.t('validacaoImagemCamadaImagem') as string)
	}
	return missing
})

/** So faz sentido quando as duas camadas existem, mas nao se encontram. */
const imageOutsideFarm = computed(
	() => Boolean(vm.imageOverlay.value) && vm.hasFarmShape.value && !vm.imageIntersectsFarm.value,
)

/**
 * O overlay do raster entra pela instancia do mapa: MapBoxSource so cobre
 * sources GeoJSON, e a imagem precisa de um source do tipo "image".
 */
function onMapLoad({ map }: { map: MapboxMap }) {
	const overlay = vm.imageOverlay.value
	const coverage = vm.imageCoverage.value

	// TEMPORARIO (diagnostico): sem isto, uma falha do Mapbox ao carregar o
	// data URI da imagem nao aparece em lugar nenhum — a camada simplesmente
	// nao pinta e o mapa segue funcionando.
	map.on('error', (event: any) => {
		const detalhe = event?.error?.message ?? String(event?.error ?? event)
		console.error('[GeoImageValidation] erro do Mapbox:', detalhe)
		diagnostico.value.erroMapbox = detalhe
		// Erros de tile do satelite nao contam: so os do source da imagem.
		if (event?.sourceId === IMAGE_SOURCE_ID) vm.imageRenderFailed.value = true
	})

	if (overlay) {
		try {
			map.addSource(IMAGE_SOURCE_ID, {
				type: 'image',
				url: overlay.dataUrl,
				coordinates: GeoImageOverlayProvider.toImageCoordinates(overlay.bounds) as any,
			})
			map.addLayer({
				id: IMAGE_SOURCE_ID,
				type: 'raster',
				source: IMAGE_SOURCE_ID,
				paint: { 'raster-opacity': 1 },
			})
			if (coverage) {
				// Mascara verde sobre os pixels com dado: acompanha o recorte real da
				// imagem, que costuma ser so os talhoes, em vez do retangulo do bbox.
				map.addSource(COVERAGE_SOURCE_ID, {
					type: 'image',
					url: coverage.dataUrl,
					coordinates: GeoImageOverlayProvider.toImageCoordinates(coverage.bounds) as any,
				})
				map.addLayer({
					id: COVERAGE_SOURCE_ID,
					type: 'raster',
					source: COVERAGE_SOURCE_ID,
					paint: { 'raster-opacity': 1 },
				})
			}

			diagnostico.value.camadaAdicionada = Boolean(map.getLayer(IMAGE_SOURCE_ID))
			if (!diagnostico.value.camadaAdicionada) vm.imageRenderFailed.value = true
			console.log('[GeoImageValidation] camada da imagem adicionada', {
				camada: diagnostico.value.camadaAdicionada,
				tamanhoDataUrlKb: Math.round(overlay.dataUrl.length / 1024),
				coordinates: GeoImageOverlayProvider.toImageCoordinates(overlay.bounds),
			})
		} catch (error) {
			console.error('[GeoImageValidation] falha ao adicionar a camada da imagem', error)
			diagnostico.value.erroMapbox = String(error)
			vm.imageRenderFailed.value = true
		}
	}

	const fitTarget = vm.clipBuffer.value ?? vm.farmMap.value?.fazenda ?? null
	if (fitTarget) {
		map.fitBounds(turfBbox(fitTarget as any) as any, { padding: 24, maxZoom: 15 })
	} else if (overlay) {
		map.fitBounds(overlay.bounds as any, { padding: 24, maxZoom: 15 })
	}

	mapInstance.value = map
	mapReady.value = true
}

return { __sfc: true,vm, IMAGE_SOURCE_ID, COVERAGE_SOURCE_ID, EMPTY_COLLECTION, mapReady, mapInstance, diagnostico, diagnosticoLinhas, completedStep, farmSpec, bufferSpec, imageBoundsSpec, enquadrarImagem, enquadrarFazenda, valueRange, missingLayers, imageOutsideFarm, onMapLoad, CLIP_BUFFER_METERS, Breadcrumb, MapBoxMap, MapBoxSource, MapBoxLayer }
}

})